import { parseAll } from '../../../../common/src/ts/parser';
import * as CFI from '../../../../common/src/ts/cfi';
import { PagesState, TabType, TabInfo, tabFocusSelf, isPageableTabInfo } from '../reducers/pages';
import { Publication, Rendition, SpineItem } from '../types/epub';
import {
  getDefaultRendition, getReflowableRendition, prevSpreadBlockPage, nextSpreadBlockPage,
} from '../utils/epub';
import { joinReference } from '../helpers/uievent';

// Utility functions related to PagesState.

export function canMoveToPrev(state: PagesState, publication: Publication): boolean {
  return getPrevPageForTab(state.tabs[state.activeTab], publication) !== null;
}

export function canMoveToNext(state: PagesState, publication: Publication): boolean {
  return getNextPageForTab(state.tabs[state.activeTab], publication) !== null;
}

export function getReference(pageIndex: number, publication: Publication): [string, string] {
  const rendition = getDefaultRendition(publication);
  const epubcfi = rendition.linearItems[pageIndex].epubcfi;

  return [rendition.opf, epubcfi];
}

export function getExtraNavItem(state: PagesState, publication: Publication): SpineItem | null {
  const rendition = state.tabs[state.activeTab].type === TabType.ReflowablePages
    ? getReflowableRendition(publication)
    : getDefaultRendition(publication);

  const { extraNav } = rendition;
  if (extraNav === undefined) {
    return null;
  }

  return rendition.itemIndices[extraNav] || null;
}

// TODO: getBookmarkReferenceを移動したらこれも同じところに移動
export function getBookmarkReferenceIfPageableTab(
  pagesState: PagesState, publication: Publication,
): string | undefined {
  const { tabs, activeTab } = pagesState;
  const tab = tabs[activeTab];

  if (isPageableTabInfo(tab)) {
    return getBookmarkReference(tab.currentPage, publication);
  }

  return undefined;
}

// FIXME: uieventからjoinReferenceをどこかに移動して、この関数も適切なところへ移動
export function getBookmarkReference(
  pageIndex: number, publication: Publication,
): string | undefined {
  const reference = getReference(pageIndex, publication);
  return joinReference(reference[0], reference[1], 'epubcfi(/4)');
}

export function findTabByID(state: PagesState, id: string): number | null {
  for (let index = 0; index < state.tabs.length; index += 1) {
    if (state.tabs[index].id === id) {
      return index;
    }
  }

  return null;
}

// for FrameStore
export function makeFrameIDForLinearPage(panelID: string, page: number): string {
  return `frame-${panelID}-${page}`;
}
export function makeFrameIDForSinglePage(panelID: string): string {
  return `frame-${panelID}`;
}
export function getActiveFrameId(state: PagesState): string {
  const tab = state.tabs[state.activeTab];
  if (tab.focus === tabFocusSelf) {
    switch (tab.type) {
      case TabType.LinearPages:
        return makeFrameIDForLinearPage(tab.id, tab.currentPage);
      case TabType.ExtraPage:
      case TabType.ReflowablePages:
      case TabType.GeneratedContent:
        return makeFrameIDForSinglePage(tab.id);
    }
  }

  if (tab.focus in state.popups) {
    return makeFrameIDForSinglePage(tab.focus);
  }
  throw new Error('Focused popup is not found.');
}
export function getActiveTabOrPopupId(state: PagesState): string {
  const tab = state.tabs[state.activeTab];
  if (tab.focus === tabFocusSelf) {
    return tab.id;
  }
  if (tab.focus in state.popups) {
    return tab.focus;
  }
  throw new Error('Focused popup is not found.');
}

export function getPrevPageForTab(tab: TabInfo, publication: Publication): number | null {
  switch (tab.type) {
    case TabType.LinearPages:
      return prevSpreadBlockPage(getDefaultRendition(publication), tab.currentPage);
    case TabType.ReflowablePages:
      return tab.currentPage > 0 ? tab.currentPage - 1 : null;
    case TabType.ExtraPage:
    case TabType.GeneratedContent:
      return null;
  }
}

export function getNextPageForTab(tab: TabInfo, publication: Publication): number | null {
  switch (tab.type) {
    case TabType.LinearPages:
      return nextSpreadBlockPage(getDefaultRendition(publication), tab.currentPage);
    case TabType.ReflowablePages:
      return tab.currentPage < getReflowableRendition(publication).linearItems.length - 1 ?
        tab.currentPage + 1 : null;
    case TabType.ExtraPage:
    case TabType.GeneratedContent:
      return null;
  }
}

export function findLinearPageByCFIPath(rendition: Rendition, cfi: CFI.Path): number {
  let pageIndex = 0;

  for (const page of rendition.linearItems) {
    const pageCFI = parseAll(page.epubcfi, CFI.path);
    if (pageCFI === undefined) {
      // FIXME : An error should be produced.
      continue;
    }

    if (cfi.compare(pageCFI) < 0) {
      break;
    }

    pageIndex = page.linearIndex;
  }

  return pageIndex;
}

function findReflowablePageByCFI(
  publication: Publication,
  sourceOPF: string,
  sourceCFIString: string,
): number {
  const destination = getReflowableRendition(publication);
  const sourceCFI = parseAll(sourceCFIString, CFI.path);
  if (sourceCFI === undefined) {
    // FIXME : An error should be produced.
    return 0;
  }

  let nearestSourceCFI: CFI.Path | null = null;
  let nearestDestinationCFI: CFI.Path | null = null;

  for (const junction of publication.mapping) {
    if (!(sourceOPF in junction) || !(destination.opf in junction)) {
      continue;
    }

    const candidateCFI = junction[sourceOPF];
    if (sourceCFI.compare(candidateCFI) < 0) {
      continue;
    }

    if (nearestSourceCFI !== null && candidateCFI.compare(nearestSourceCFI) < 0) {
      continue;
    }

    nearestSourceCFI = candidateCFI;
    nearestDestinationCFI = junction[destination.opf];
  }

  return (
    nearestDestinationCFI === null
      ? 0
      : findLinearPageByCFIPath(destination, nearestDestinationCFI)
  );
}

export function getReflowablePageForTab(tab: TabInfo, publication: Publication): number | null {
  switch (tab.type) {
    case TabType.LinearPages: {
      // TODO : Support non-default rendition if needed.
      const source = getDefaultRendition(publication);
      return findReflowablePageByCFI(
        publication,
        source.opf,
        source.linearItems[tab.currentPage].epubcfi,
      );
    }

    case TabType.ReflowablePages:
      return tab.currentPage;

    case TabType.ExtraPage: {
      // TODO : Support non-default rendition if needed.
      const source = getDefaultRendition(publication);
      return findReflowablePageByCFI(
        publication,
        source.opf,
        source.itemIndices[tab.path].epubcfi,
      );
    }

    case TabType.GeneratedContent:
      // TODO
      return null;
  }
}
